shadow$provide[0] = function(require,module,exports) {
"use strict";
/**
 * Network display currency.
 *
 * Each network may set a default currency (an ISO 4217 code) that every
 * monetary amount in the UI is displayed in. It is display only: amounts are
 * never converted. The backend validates the setting and sends it as
 * `networkcurrency` with the user and network info, or nil when the network
 * sets no valid currency; the legacy ClojureScript app pushes it here with
 * `setNetworkCurrency` so that TypeScript and ClojureScript format money from
 * the same place.
 *
 * When a currency is set, amounts are formatted with `Intl.NumberFormat` in
 * the viewer's browser locale, so separators, symbol position and fraction
 * digits follow the locale and the currency. The symbol is always the
 * currency's short one ("$" for USD in every locale, not "US$"), because a
 * network shows one currency and there is nothing to tell it apart from.
 *
 * When no currency is set, amounts are formatted as US dollars in en-US for
 * every viewer, whatever their browser locale, exactly as before network
 * currencies existed.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.DEFAULT_CURRENCY = void 0;
exports.formatCurrency = formatCurrency;
exports.getCurrencyFractionDigits = getCurrencyFractionDigits;
exports.getCurrencyNumberFormat = getCurrencyNumberFormat;
exports.getCurrencySymbol = getCurrencySymbol;
exports.getDisplayLocale = getDisplayLocale;
exports.getNetworkCurrency = getNetworkCurrency;
exports.normalizeCurrency = normalizeCurrency;
exports.setNetworkCurrency = setNetworkCurrency;
exports.subscribeNetworkCurrency = subscribeNetworkCurrency;
const DEFAULT_CURRENCY = 'USD';
exports.DEFAULT_CURRENCY = DEFAULT_CURRENCY;
const DEFAULT_LOCALE = 'en-US';
// Validating a code builds an `Intl.NumberFormat`, and it runs for every
// formatted amount, so the verdict is remembered. Only well-formed codes are
// stored (at most 26^3 of them), so the map cannot grow without bound.
const currencyCodeCache = new Map();
/**
 * Returns `code` as an upper-case ISO 4217 code when `Intl.NumberFormat`
 * accepts it as a currency, otherwise undefined (no currency set).
 */
function normalizeCurrency(code) {
    if (typeof code !== 'string')
        return undefined;
    const candidate = code.trim().toUpperCase();
    if (!/^[A-Z]{3}$/.test(candidate))
        return undefined;
    if (currencyCodeCache.has(candidate))
        return currencyCodeCache.get(candidate);
    let normalized;
    try {
        new Intl.NumberFormat(DEFAULT_LOCALE, { style: 'currency', currency: candidate });
        normalized = candidate;
    }
    catch (_a) {
        normalized = undefined;
    }
    currencyCodeCache.set(candidate, normalized);
    return normalized;
}
// `supportedLocalesOf` runs for every formatted amount, so the locale `Intl`
// resolves for a browser language is remembered. The browser language is still
// read on every call, so a change to it is picked up.
const displayLocaleCache = new Map();
/**
 * Returns the viewer's preferred browser locale when `Intl` supports it,
 * otherwise en-US.
 */
function getDisplayLocale() {
    const nav = typeof navigator === 'undefined' ? undefined : navigator;
    const preferred = ((nav === null || nav === void 0 ? void 0 : nav.languages) && nav.languages[0]) || (nav === null || nav === void 0 ? void 0 : nav.language);
    if (!preferred)
        return DEFAULT_LOCALE;
    let locale = displayLocaleCache.get(preferred);
    if (locale === undefined) {
        let supported;
        try {
            [supported] = Intl.NumberFormat.supportedLocalesOf([preferred]);
        }
        catch (_a) {
            // Fall through to the default locale.
        }
        locale = supported || DEFAULT_LOCALE;
        displayLocaleCache.set(preferred, locale);
    }
    return locale;
}
let networkCurrency;
const listeners = new Set();
/**
 * Sets the network currency. Anything that is not a valid code means the
 * network sets no currency.
 */
function setNetworkCurrency(code) {
    const next = normalizeCurrency(code);
    if (next === networkCurrency)
        return;
    networkCurrency = next;
    listeners.forEach((listener) => listener(next));
}
/**
 * Returns the network currency, or undefined when the network sets none.
 */
function getNetworkCurrency() {
    return networkCurrency;
}
/**
 * Calls `listener` whenever the network currency changes. Returns a function
 * that unsubscribes.
 */
function subscribeNetworkCurrency(listener) {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}
const formatCache = new Map();
/**
 * Returns an `Intl.NumberFormat` that shows the currency's short symbol, or
 * its default symbol on an engine that does not support `narrowSymbol`.
 * Throws when `locale` is not usable.
 */
function createCurrencyNumberFormat(locale, options) {
    try {
        const narrow = Object.assign(Object.assign({}, options), { style: 'currency', currencyDisplay: 'narrowSymbol' });
        return new Intl.NumberFormat(locale, narrow);
    }
    catch (_a) {
        return new Intl.NumberFormat(locale, Object.assign(Object.assign({}, options), { style: 'currency' }));
    }
}
/**
 * Returns a (cached) `Intl.NumberFormat` for a currency amount.
 *
 * With a valid currency (passed in `options`, or else set on the network) it
 * uses the browser locale and the currency's short symbol. A currency passed in
 * `options` that is not valid counts as none passed. Without a valid currency it
 * formats US dollars in en-US (or in `options.locale`) with the default symbol,
 * as the UI always did before network currencies.
 */
function getCurrencyNumberFormat(options = {}) {
    var _a, _b, _c;
    const currency = (_a = normalizeCurrency(options.currency)) !== null && _a !== void 0 ? _a : networkCurrency;
    const { minimumFractionDigits, maximumFractionDigits } = options;
    if (!currency) {
        const noCurrencyLocale = (_b = options.locale) !== null && _b !== void 0 ? _b : DEFAULT_LOCALE;
        const noCurrencyKey = JSON.stringify([
            'no-currency',
            noCurrencyLocale,
            minimumFractionDigits,
            maximumFractionDigits,
        ]);
        let noCurrencyFormat = formatCache.get(noCurrencyKey);
        if (!noCurrencyFormat) {
            const noCurrencyOptions = {
                style: 'currency',
                currency: DEFAULT_CURRENCY,
                minimumFractionDigits,
                maximumFractionDigits,
            };
            try {
                noCurrencyFormat = new Intl.NumberFormat(noCurrencyLocale, noCurrencyOptions);
            }
            catch (_d) {
                noCurrencyFormat = new Intl.NumberFormat(DEFAULT_LOCALE, noCurrencyOptions);
            }
            formatCache.set(noCurrencyKey, noCurrencyFormat);
        }
        return noCurrencyFormat;
    }
    const locale = (_c = options.locale) !== null && _c !== void 0 ? _c : getDisplayLocale();
    const key = JSON.stringify([locale, currency, minimumFractionDigits, maximumFractionDigits]);
    let format = formatCache.get(key);
    if (!format) {
        const formatOptions = { currency, minimumFractionDigits, maximumFractionDigits };
        try {
            format = createCurrencyNumberFormat(locale, formatOptions);
        }
        catch (_e) {
            format = createCurrencyNumberFormat(DEFAULT_LOCALE, formatOptions);
        }
        formatCache.set(key, format);
    }
    return format;
}
/**
 * Formats `amount` as money in the network currency and browser locale, with
 * the currency's short symbol, or as US dollars in en-US when the network sets
 * no currency.
 */
function formatCurrency(amount, options) {
    return getCurrencyNumberFormat(options).format(amount);
}
/**
 * Returns how many fraction digits a currency is normally shown with: 2 for
 * USD, EUR and GBP, 0 for JPY and KRW, 3 for KWD. No currency, or one that is
 * not valid, means the network currency, or USD when the network sets none.
 */
function getCurrencyFractionDigits(currency) {
    var _a;
    return (_a = getCurrencyNumberFormat({ currency, locale: DEFAULT_LOCALE }).resolvedOptions().maximumFractionDigits) !== null && _a !== void 0 ? _a : 2;
}
/**
 * Returns the short symbol the browser locale shows for a currency, for
 * example "$" for USD (in en-GB as well as en-US), "€" for EUR, "£" for GBP
 * and "¥" for JPY. A currency with no symbol of its own shows its code. No
 * currency, on a network that sets none, shows "$".
 */
function getCurrencySymbol(options) {
    var _a;
    const format = getCurrencyNumberFormat(options);
    const part = format.formatToParts(0).find((p) => p.type === 'currency');
    return part ? part.value : ((_a = format.resolvedOptions().currency) !== null && _a !== void 0 ? _a : DEFAULT_CURRENCY);
}
//# sourceMappingURL=currency.js.map
};